import useRulePageMode from '../hooks/useRulePageMode';
import { useCurrentNetwork } from '../../../hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getReferencesOptions, notificationChannelOptions } from '../api';
import Select from '../../../components/common/Select';
import type { FC } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, FormControl, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import Input from '../../../components/common/Input';
import type { Option } from '../../../types/shared';
import Autocomplete from '../../../components/common/Autocomplete';
import { isEmpty } from 'lodash-es';

export const NotificationLevelSelect = () => {
  const { isRead } = useRulePageMode();
  const network = useCurrentNetwork();
  const { data: notificationLevels } = useSuspenseQuery({
    ...getReferencesOptions(network),
    select: (data) => data.notification_levels.map(({ name, id }) => ({ value: id, label: name })),
  });

  return (
    <Select
      name="notificationLevel"
      inputLabel="notification_level"
      selectAll={false}
      options={notificationLevels}
      disabled={isRead}
    />
  );
};

export const NotificationMessageFields: FC<{ ruleId: number }> = ({ ruleId }) => {
  const { isRead } = useRulePageMode();
  return (
    <Accordion
      disableGutters
      sx={(t) => ({
        borderBottom: `1px solid ${t.palette.divider}`,
        '&:last-of-type': { borderRadius: 0 },
        pb: 1.5,
      })}
    >
      <AccordionSummary
        sx={{ py: 0, pl: 1.5, pr: 1, minHeight: 32, '& .MuiAccordionSummary-content': { my: 0 } }}
        expandIcon={<ExpandMore />}
        id={`accordionPanel_${ruleId}`}
      >
        <Typography variant="subtitle2" sx={(t) => ({ color: t.palette.text.secondary })}>
          Notification message
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
          <Input name="notificationMessage.title" label="notification title" innerLabel disabled={isRead} />
          <Input name="notificationMessage.body" label="notification body" innerLabel multiline disabled={isRead} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

interface NotificationChannelsAutocompleteInputProps {
  label?: string;
  inputLabel?: string;
  name: string;
  required?: boolean;
}

export const NotificationChannelsAutocomplete: FC<NotificationChannelsAutocompleteInputProps> = ({
  name,
  label,
  inputLabel,
  required,
}) => {
  const { isRead } = useRulePageMode();
  const { data: notificationChannels } = useSuspenseQuery({
    ...notificationChannelOptions,
    select: (data): Option[] => {
      return data.map(({ id, name }) => ({ value: id, label: name }));
    },
  });

  return (
    <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
      {label && !inputLabel && (
        <Typography variant="subtitle2" mb={1}>
          {label}
          {required && '*'}
        </Typography>
      )}
      <FormControl sx={{ flex: 1 }}>
        <Autocomplete
          InputProps={{ disabled: isEmpty(notificationChannels) || isRead, label: inputLabel ?? '' }}
          options={notificationChannels ?? []}
          allSelectable={false}
          name={name}
          id={name}
          label={inputLabel}
          innerLabel
        />
      </FormControl>
    </Box>
  );
};
